import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { Checkbox } from '../../components/ui/Checkbox';
import ClientFormModal from './components/ClientFormModal';
import DeleteClientModal from './components/DeleteClientModal';
import clientService from '../../services/clientService';

const ClientManagementDatabase = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await clientService?.getAllClients();
    if (error) {
      setError(error);
      setClients([]);
    } else {
      const transformedData = data?.map(client => ({
        id: client?.id,
        companyName: client?.company_name,
        contactPerson: client?.contact_person,
        email: client?.email,
        phone: client?.phone,
        address: client?.address,
        industry: client?.industry,
        companySize: client?.company_size,
        taxId: client?.tax_id,
        billingAddress: client?.billing_address,
        notes: client?.notes,
        status: client?.status,
        createdAt: client?.created_at,
        updatedAt: client?.updated_at
      })) || [];
      setClients(transformedData);
      setFilteredClients(transformedData);
    }
    setLoading(false);
  };

  useEffect(() => {
    let result = clients;

    if (filterIndustry !== 'all') {
      result = result?.filter(c => c?.industry === filterIndustry);
    }

    if (filterStatus !== 'all') {
      result = result?.filter(c => c?.status === filterStatus);
    }

    if (searchQuery) {
      result = result?.filter(c =>
        c?.companyName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        c?.contactPerson?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        c?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredClients(result);
  }, [clients, searchQuery, filterIndustry, filterStatus]);

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev =>
      prev?.includes(clientId) ?
      prev?.filter(id => id !== clientId) :
      [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients?.length === filteredClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients?.map(c => c?.id));
    }
  };

  const handleCreateClient = async (clientData) => {
    const { data, error } = await clientService?.createClient(clientData);
    if (error) {
      alert(`Error creating client: ${error}`);
    } else {
      await fetchClients();
      setShowCreateModal(false);
    }
  };

  const handleUpdateClient = async (clientData) => {
    if (!editingClient) return;
    const { data, error } = await clientService?.updateClient(editingClient?.id, clientData);
    if (error) {
      alert(`Error updating client: ${error}`);
    } else {
      await fetchClients();
      setShowCreateModal(false);
      setEditingClient(null);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowCreateModal(true);
  };

  const handleDeleteClient = (client) => {
    setDeletingClient(client);
  };

  const handleConfirmDelete = async (clientId) => {
    const { error } = await clientService?.deleteClient(clientId);
    if (error) {
      alert(`Error deleting client: ${error}`);
    } else {
      await fetchClients();
      setDeletingClient(null);
    }
  };

  const industryOptions = [
    { value: 'all', label: 'All Industries' },
    { value: 'Construction', label: 'Construction' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Real Estate', label: 'Real Estate' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Healthcare', label: 'Healthcare' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <QuickActionsToolbar />
      <div className={`transition-all duration-250 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center gap-4">
              <button
                className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                aria-label="Toggle sidebar"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Client Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Manage client information and relationships
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter />
              <Button
                onClick={() => {
                  setEditingClient(null);
                  setShowCreateModal(true);
                }}
                className="flex items-center gap-2"
              >
                <Icon name="Plus" size={18} />
                <span className="hidden md:inline">Create Client</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="space-y-4 md:space-y-6">
            {/* Filters and Search */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  type="search"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                />
                <Select
                  options={industryOptions}
                  value={filterIndustry}
                  onChange={(e) => setFilterIndustry(e?.target?.value)}
                />
                <Select
                  options={statusOptions}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                />
              </div>
              {selectedClients?.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedClients?.length} selected
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedClients([])}
                    className="flex items-center gap-2"
                  >
                    <Icon name="X" size={16} />
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* Client List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
                <p className="text-destructive">Error loading clients: {error}</p>
              </div>
            ) : filteredClients?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No clients found</h3>
                <p className="text-muted-foreground mb-4">Get started by creating your first client</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Icon name="Plus" size={18} className="mr-2" />
                  Create Client
                </Button>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <Checkbox
                            checked={selectedClients?.length === filteredClients?.length}
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Company</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Contact Person</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Industry</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Created</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients?.map((client) => (
                        <tr key={client?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={selectedClients?.includes(client?.id)}
                              onCheckedChange={() => handleSelectClient(client?.id)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground">{client?.companyName}</div>
                            <div className="text-xs text-muted-foreground">{client?.companySize}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">{client?.contactPerson || '-'}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{client?.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{client?.phone || '-'}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{client?.industry || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              client?.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'
                            }`}>
                              {client?.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(client?.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                className="p-1 hover:bg-muted rounded transition-colors"
                                onClick={() => handleEditClient(client)}
                              >
                                <Icon name="Edit" size={16} />
                              </button>
                              <button
                                className="p-1 hover:bg-destructive/10 rounded transition-colors text-destructive"
                                onClick={() => handleDeleteClient(client)}
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {showCreateModal && (
        <ClientFormModal
          onClose={() => {
            setShowCreateModal(false);
            setEditingClient(null);
          }}
          onSubmit={editingClient ? handleUpdateClient : handleCreateClient}
          editingClient={editingClient}
        />
      )}

      {deletingClient && (
        <DeleteClientModal
          client={deletingClient}
          onClose={() => setDeletingClient(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default ClientManagementDatabase;